import { CROPS } from '@/data/crops';
import { ITEMS, type ItemId, cropOf, furnitureOf, produceFor } from '@/data/items';
import type { Season } from '@/time/clock';
import type { Farmland } from '@/world/farmland';
import { FURNITURE, type FurnitureLayer } from '@/world/furniture';
import type { Inventory } from '@/world/inventory';
import type { QuickBag } from '@/world/quickBag';
import type { Livestock } from '@/world/livestock';
import type { WaterKind } from '@/data/fish';
import { isRodId } from '@/data/tackle';
import { type Tilemap, waterKindOf } from '@/world/tilemap';

/**
 * What pressing Use does, given where Katie stands and what she is holding.
 *
 * One resolver rather than a branch per tool: the outcome depends on the item,
 * the tile and the plot together, and splitting that across three call sites is
 * how "the hoe works but only sometimes" bugs happen. Returning a message also
 * means the UI never has to re-derive why nothing happened.
 */

export type FarmOutcome =
  | { kind: 'tilled' }
  | { kind: 'planted'; cropId: string }
  | { kind: 'watered' }
  | { kind: 'harvested'; cropId: string; amount: number; tier: string; overflowed: boolean }
  | { kind: 'fed'; name: string }
  | { kind: 'collected'; name: string; overflowed: boolean }
  | { kind: 'placed'; name: string }
  | { kind: 'retrieved'; name: string }
  | { kind: 'fish'; water: WaterKind; rod: ItemId }
  | { kind: 'gathered'; item: string }
  | { kind: 'nothing'; reason: string };

export interface FarmContext {
  readonly map: Tilemap;
  /** Null where crops do not belong: indoors, the shore, underground. */
  readonly farmland: Farmland | null;
  readonly inventory: Inventory;
  /** What is to hand. The backpack is storage; the bag says what is held. */
  readonly quickBag: QuickBag;
  readonly season: Season;
  readonly dayIndex: number;
  /** Tile Katie is facing. */
  readonly x: number;
  readonly y: number;
  readonly furniture?: FurnitureLayer;
  readonly livestock?: Livestock | null;
  /** Tool in hand. Used when the selected bag slot has nothing to offer. */
  readonly activeTool?: ItemId | null;
  /** Injectable randomness, so gathering is testable. */
  readonly random?: () => number;
}

/**
 * True when a bag item is used *on* a tile rather than being inert cargo.
 *
 * Exported because the tile cursor has to know whether anything in hand
 * targets a tile before it will draw. Keeping the rule here means the cursor
 * can never disagree with what Use actually does.
 */
export function isPlaceable(id: ItemId | null): boolean {
  if (!id) return false;
  const kind = ITEMS[id].kind;
  return kind === 'seed' || kind === 'furniture' || id === 'feed';
}

export function useHeldItem(context: FarmContext): FarmOutcome {
  const { farmland, inventory, quickBag, livestock, x, y } = context;

  // Everything below is ordered by what the player most likely meant, so the
  // common case never needs a tool swap first.

  // 1. A ripe crop under the cursor.
  if (farmland?.isRipe(x, y)) return harvest(context);

  // 2. An animal in reach, waiting to be collected from or fed.
  const animal = livestock?.nearestTo(x, y) ?? null;
  if (animal?.hasProduce) {
    animal.collect();
    const leftover = inventory.add('egg', 1);
    // A feather now and then, which is what fly lures are tied from.
    if ((context.random ?? Math.random)() < 0.3) inventory.add('mat:feather', 1);
    return { kind: 'collected', name: animal.name, overflowed: leftover > 0 };
  }

  const held = quickBag.heldItem(inventory);

  // 3. Something in the bag slot that is used *on* the world — a seed, a piece
  //    of furniture, feed for the animals. These beat the tool, because
  //    choosing them is the more specific act.
  const heldKind = held ? ITEMS[held].kind : null;
  const heldIsPlaceable = isPlaceable(held);

  if (!held || !heldIsPlaceable) {
    // 4. Otherwise the tool decides. Furniture is lifted only when there is no
    //    tool that would rather act on this tile.
    const tool = context.activeTool ?? null;
    if (tool) {
      const outcome = useTool(context, tool);
      if (outcome.kind !== 'nothing') return outcome;

      const retrieved = retrieveFurniture(context);
      if (retrieved) return retrieved;
      return outcome;
    }

    const retrieved = retrieveFurniture(context);
    if (retrieved) return retrieved;
    return { kind: 'nothing', reason: 'Nothing in hand' };
  }

  if (held === 'feed' && animal) {
    if (!animal.feed(context.dayIndex)) {
      return { kind: 'nothing', reason: `${animal.name} has already eaten` };
    }
    quickBag.consumeHeld(inventory, 1);
    return { kind: 'fed', name: animal.name };
  }

  switch (heldKind) {
    case 'seed':
      return plant(context, held);
    case 'furniture':
      return placeFurniture(context, held);
    default:
      return { kind: 'nothing', reason: 'Nothing to do with that here' };
  }
}

function placeFurniture(context: FarmContext, item: ItemId): FarmOutcome {
  const { furniture, map, inventory, quickBag, x, y } = context;
  const id = furnitureOf(item);
  if (!furniture || !id) return { kind: 'nothing', reason: 'Cannot put that here' };

  if (map.isSolidTile(x, y)) return { kind: 'nothing', reason: 'Not enough room' };
  if (!furniture.place(x, y, id)) {
    return { kind: 'nothing', reason: 'Something is already there' };
  }

  quickBag.consumeHeld(inventory, 1);
  return { kind: 'placed', name: FURNITURE[id].name };
}

function retrieveFurniture(context: FarmContext): FarmOutcome | null {
  const { furniture, inventory, x, y } = context;
  const id = furniture?.at(x, y);
  if (!furniture || !id) return null;

  // Only lift it if there is somewhere to put it, so a full backpack cannot
  // delete the player's furniture. Reachable now that the backpack is storage
  // in its own right: it can be full while your hands are empty.
  if (inventory.add(`furniture:${id}`, 1) > 0) {
    return { kind: 'nothing', reason: 'Your bag is full' };
  }
  furniture.remove(x, y);
  return { kind: 'retrieved', name: FURNITURE[id].name };
}

/**
 * Weighted single draw from a table of chances.
 *
 * `random` is injectable so gathering is testable; the chances are checked in
 * order and the first hit wins, so rarer finds go last.
 */
function rollGather(
  context: FarmContext,
  table: readonly { item: ItemId; chance: number }[],
): ItemId | null {
  const roll = (context.random ?? Math.random)();
  let threshold = 0;
  for (const entry of table) {
    threshold += entry.chance;
    if (roll < threshold) return entry.item;
  }
  return null;
}

/** Digging the beach: shells, the occasional pearl, and bait. */
function digSand(context: FarmContext, wet: boolean): FarmOutcome {
  const find = rollGather(context, [
    { item: 'mat:shell', chance: wet ? 0.45 : 0.3 },
    { item: 'bait:worm', chance: 0.2 },
    { item: 'bait:shrimp', chance: wet ? 0.2 : 0.1 },
    { item: 'mat:pearl', chance: 0.03 },
  ]);

  if (!find) return { kind: 'nothing', reason: 'Nothing but sand' };
  context.inventory.add(find, 1);
  return { kind: 'gathered', item: find };
}

function useTool(context: FarmContext, tool: ItemId): FarmOutcome {
  const { map, farmland, inventory, dayIndex, x, y } = context;

  if (isRodId(tool)) {
    // Water is solid to walk on, so "facing water" is exactly the tile ahead.
    const water = waterKindOf(map.at(x, y));
    return water
      ? { kind: 'fish', water, rod: tool }
      : { kind: 'nothing', reason: 'No water in front of you' };
  }

  if (tool === 'hoe') {
    // Sand is dug for what is buried in it rather than farmed.
    const beach = map.at(x, y);
    if (beach === 's' || beach === 'S') return digSand(context, beach === 'S');

    if (!farmland) return { kind: 'nothing', reason: 'This is no place for a field' };
    if (!map.kindAt(x, y).tillable) return { kind: 'nothing', reason: 'Cannot till here' };
    if (farmland.at(x, y)?.crop) return { kind: 'nothing', reason: 'Something is growing here' };
    if (!farmland.till(x, y)) return { kind: 'nothing', reason: 'Already tilled' };

    // Breaking ground turns things up. This is the main source of fiber and
    // worms, which ties tackle progression to the farming routine rather than
    // to a separate errand.
    const find = rollGather(context, [
      { item: 'mat:fiber', chance: 0.4 },
      { item: 'bait:worm', chance: 0.3 },
      { item: 'bait:grub', chance: 0.15 },
    ]);
    if (find) inventory.add(find, 1);
    return { kind: 'tilled' };
  }

  if (tool === 'wateringCan') {
    if (!farmland?.at(x, y)?.tilled) return { kind: 'nothing', reason: 'Nothing to water' };
    return farmland.water(x, y, dayIndex)
      ? { kind: 'watered' }
      : { kind: 'nothing', reason: 'Already watered' };
  }

  return { kind: 'nothing', reason: 'Cannot use that here' };
}

function plant(context: FarmContext, seed: ItemId): FarmOutcome {
  const { farmland, inventory, quickBag, season, x, y } = context;

  const cropId = cropOf(seed);
  if (!cropId) return { kind: 'nothing', reason: 'Not a seed' };
  if (!farmland) return { kind: 'nothing', reason: 'This is no place for a field' };

  const result = farmland.plant(x, y, cropId, season);
  switch (result) {
    case 'planted':
      quickBag.consumeHeld(inventory, 1);
      return { kind: 'planted', cropId };
    case 'not-tilled':
      return { kind: 'nothing', reason: 'Till the soil first' };
    case 'occupied':
      return { kind: 'nothing', reason: 'Something is already growing' };
    case 'wrong-season':
      return {
        kind: 'nothing',
        reason: `${CROPS[cropId].name} will not grow in ${season}`,
      };
  }
}

function harvest(context: FarmContext): FarmOutcome {
  const { farmland, inventory, x, y } = context;
  if (!farmland) return { kind: 'nothing', reason: 'Nothing to harvest' };

  const picked = farmland.harvest(x, y);
  if (!picked) return { kind: 'nothing', reason: 'Not ready' };

  const leftover = inventory.add(produceFor(picked.cropId), picked.amount);
  return {
    kind: 'harvested',
    cropId: picked.cropId,
    amount: picked.amount,
    tier: picked.tier,
    // Surfaced rather than swallowed: produce that vanished into a full bag
    // with no feedback reads as the game losing the player's work.
    overflowed: leftover > 0,
  };
}
